import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardActions, Button, TextField, Typography, Box, Chip, RadioGroup, FormControlLabel, Radio, Alert } from '@mui/material';
import { BookOpen, Award } from 'lucide-react';
import katex from 'katex';
import 'katex/dist/katex.min.css';
import axios from 'axios';
import MathDiagram from './diagrams/MathDiagram';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Tooltip, IconButton } from '@mui/material';
import ReportContentDialog from './educational/ReportContentDialog';

// Helper: safely extract text from string or object {text, penalty, _id}
const safeText = (val) => {
  if (!val) return '';
  if (typeof val === 'string') return val;
  if (typeof val === 'object') return val.latex || val.text || '';
  return String(val);
};

const QuestionDisplay = ({ question, onAnswerSubmit, currentUser }) => {
  const [currentQuestion, setCurrentQuestion] = useState(question);
  const [answeredQuestions, setAnsweredQuestions] = useState([]);
  const [userAnswer, setUserAnswer] = useState('');
  const [selectedOption, setSelectedOption] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isCorrect, setIsCorrect] = useState(null);
  const [showSolution, setShowSolution] = useState(false);
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [reportOpen, setReportOpen] = useState(false);
  const [hasReported, setHasReported] = useState(false);
  const handleReportClose = (wasSuccessful) => {
    setReportOpen(false);
    if (wasSuccessful) setHasReported(true);
  };

  useEffect(() => {
    if (question && question._id !== currentQuestion?._id) {
      setCurrentQuestion(question);
      handleReset();
    }
  }, [question]);

  // Render LaTeX content
  const renderLatex = (text) => {
    if (!text) return '';
    
    // Fix: handle all object formats
    if (typeof text === 'object' && text !== null) {
      text = text.latex || text.text || text.value || text.explanation || '';
    }
    if (!text) return '';
    text = String(text);
    
    let processedText = String(text);
    
    // Handle block LaTeX ($$...$$)
    processedText = processedText.replace(/\$\$([^$]+)\$\$/g, (match, p1) => {
      try {
        return katex.renderToString(p1, { displayMode: true, throwOnError: false });
      } catch (e) {
        return match;
      }
    });
    
    // Handle inline LaTeX ($...$)
    processedText = processedText.replace(/\$([^$]+)\$/g, (match, p1) => {
      try {
        return katex.renderToString(p1, { throwOnError: false });
      } catch (e) {
        return match;
      }
    });
    
    // If text contains LaTeX commands but no $, try to render it directly
    if (processedText === String(text) && 
        (text.includes('\\') || text.includes('^') || text.includes('_'))) {
      try {
        return <span dangerouslySetInnerHTML={{ __html: katex.renderToString(text, { throwOnError: false }) }} />;
      } catch (e) {
        return <span>{text}</span>;
      }
    }
    
    return <span dangerouslySetInnerHTML={{ __html: processedText }} />;
  };

  const handleSubmit = async () => {
    if (!userAnswer && !selectedOption) {
      setFeedback('Please provide an answer before submitting.');
      return;
    }

    setLoading(true);
    
    try {
      const answerToSubmit = currentQuestion.solutionType === 'multipleChoice' || currentQuestion.type === 'multiple-choice' || currentQuestion.questionType === 'multiple-choice'
        ? selectedOption 
        : userAnswer;

      const questionId = currentQuestion._id || currentQuestion.id || 'unknown';
      const token = localStorage.getItem('token');
      
      const response = await axios.post(
        `/api/questions/${questionId}/answer`,
        { answer: answerToSubmit },
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );

      setIsCorrect(response.data.correct);
      setFeedback(response.data.feedback || (response.data.correct ? 'Correct! Well done!' : 'Incorrect. Try reviewing the solution.'));
      setIsSubmitted(true);

      if (onAnswerSubmit) {
        onAnswerSubmit({
          questionId: questionId,
          correct: response.data.correct,
          answer: answerToSubmit
        });
      }
    } catch (error) {
      console.error('Error submitting answer:', error);
      setFeedback('Error submitting answer. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchNewQuestion = async () => {
    setLoading(true);
    setFeedback('Loading new question...');
    
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const grade = currentQuestion?.grade || user.grade || 10;
      
      const currentQuestionId = currentQuestion._id || currentQuestion.id;
      const excludeList = [...answeredQuestions, currentQuestionId].filter(Boolean);
      
      const response = await axios.get('/api/questions', {
        params: { grade: grade, limit: 50 }
      });
      
      let questions = [];
      
      if (Array.isArray(response.data)) {
        questions = response.data;
      } else if (response.data && Array.isArray(response.data.questions)) {
        questions = response.data.questions;
      } else if (response.data && Array.isArray(response.data.data)) {
        questions = response.data.data;
      } else if (response.data && response.data._id) {
        questions = [response.data];
      }
      
      if (questions.length > 0) {
        const availableQuestions = questions.filter(
          q => !excludeList.includes(q._id || q.id)
        );
        
        if (availableQuestions.length > 0) {
          const randomIndex = Math.floor(Math.random() * availableQuestions.length);
          const newQuestion = availableQuestions[randomIndex];
          
          setCurrentQuestion(newQuestion);
          setAnsweredQuestions(prev => [...prev, currentQuestionId]);
          handleReset();
          
          if (onAnswerSubmit) {
            onAnswerSubmit({ type: 'new_question', question: newQuestion });
          }
        } else {
          setAnsweredQuestions([]);
          setFeedback('All questions completed! Starting over...');
          const randomIndex = Math.floor(Math.random() * questions.length);
          setCurrentQuestion(questions[randomIndex]);
          handleReset();
        }
      } else {
        setFeedback('No questions available.');
      }
    } catch (error) {
      console.error('Error fetching new question:', error);
      setFeedback('Failed to load new question. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setUserAnswer('');
    setSelectedOption('');
    setIsSubmitted(false);
    setIsCorrect(null);
    setShowSolution(false);
    setFeedback('');
  };

  const renderAnswerInput = () => {
    if (isSubmitted) {
      return (
        <Box sx={{ mt: 2 }}>
          <Alert severity={isCorrect ? 'success' : 'error'}>
            {feedback}
          </Alert>
          {!isCorrect && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              Your answer: <strong>{currentQuestion.solutionType === 'multipleChoice' || currentQuestion.type === 'multiple-choice' || currentQuestion.questionType === 'multiple-choice' ? safeText(selectedOption) : userAnswer}</strong>
            </Typography>
          )}
        </Box>
      );
    }

    const qType = currentQuestion.solutionType || currentQuestion.type || currentQuestion.questionType || 'text';

    switch (qType) {
      case 'multipleChoice':
      case 'multiple-choice':
        return (
          <RadioGroup
            value={selectedOption}
            onChange={(e) => setSelectedOption(e.target.value)}
          >
            {currentQuestion.options?.map((option, index) => (
              <FormControlLabel
                key={index}
                value={typeof option === 'object' ? (option.text || option.latex || option.value) : option}
                control={<Radio />}
                label={typeof option === 'object' 
                  ? (option.latex ? renderLatex(`$${option.latex}$`) : safeText(option.text || option.value))
                  : String(option)
                }
              />
            ))}
          </RadioGroup>
        );
      
      case 'numerical':
        return (
          <TextField
            fullWidth
            variant="outlined"
            label="Enter your numerical answer"
            value={userAnswer}
            onChange={(e) => setUserAnswer(e.target.value)}
            type="number"
            disabled={isSubmitted}
            placeholder="e.g., 42"
          />
        );
      
      case 'algebraic':
        return (
          <TextField
            fullWidth
            variant="outlined"
            label="Enter your algebraic expression"
            value={userAnswer}
            onChange={(e) => setUserAnswer(e.target.value)}
            disabled={isSubmitted}
            placeholder="e.g., x^2 + 2x + 1 or 4x"
            helperText="Use ^ for exponents, e.g., x^2 for x²"
          />
        );
      
      default:
        return (
          <TextField
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            label="Enter your answer"
            value={userAnswer}
            onChange={(e) => setUserAnswer(e.target.value)}
            disabled={isSubmitted}
            placeholder="Type your answer here..."
          />
        );
    }
  };

  const renderSolution = () => {
    if (!showSolution) return null;

    // Get solution data from various possible locations in the question object
    const solution = currentQuestion.solution || {};
    let rawCorrectAnswer = currentQuestion.correctAnswer || solution.answer || solution.correctAnswer || '';
    const correctAnswer = (typeof rawCorrectAnswer === 'object' && rawCorrectAnswer !== null) 
      ? (rawCorrectAnswer.latex || rawCorrectAnswer.text || rawCorrectAnswer.value || '') 
      : String(rawCorrectAnswer || '');
    let rawSolutionText = currentQuestion.solutionText || solution.explanation || solution.text || '';
    const solutionText = (typeof rawSolutionText === 'object' && rawSolutionText !== null)
      ? (rawSolutionText.text || rawSolutionText.latex || '')
      : String(rawSolutionText || '');
    
    // Get steps from various possible locations
    let steps = [];
    if (Array.isArray(solution.steps)) {
      // Ensure step text fields are strings, not objects
      steps = solution.steps;
    } else if (Array.isArray(currentQuestion.steps)) {
      steps = currentQuestion.steps;
    } else if (Array.isArray(currentQuestion.solutionSteps)) {
      steps = currentQuestion.solutionSteps;
    }

    // If solution is a string, parse it for steps
    if (typeof currentQuestion.solution === 'string' && currentQuestion.solution.includes('Step')) {
      const stepMatches = currentQuestion.solution.match(/Step \d+:.*?(?=Step \d+:|$)/gs);
      if (stepMatches) {
        steps = stepMatches.map((s, i) => ({ content: s.trim() }));
      }
    }

    // Check if we have any solution content
    const hasSolutionContent = correctAnswer || solutionText || steps.length > 0;

    if (!hasSolutionContent) {
      return (
        <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
          <Typography variant="h6" gutterBottom>
            <BookOpen size={20} style={{ marginRight: 8, verticalAlign: 'middle' }} />
            Solution
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Solution not available for this question.
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ mt: 3, p: 2, bgcolor: '#f0fdf4', borderRadius: 1, border: '1px solid #86efac' }}>
        <Typography variant="h6" gutterBottom sx={{ color: '#166534' }}>
          <BookOpen size={20} style={{ marginRight: 8, verticalAlign: 'middle' }} />
          Solution
        </Typography>
        
        {/* Correct Answer */}
        {correctAnswer && (
          <Box sx={{ mb: 2, p: 1.5, bgcolor: 'white', borderRadius: 1, border: '1px solid #bbf7d0' }}>
            <Typography variant="subtitle2" color="text.secondary">
              Correct Answer:
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#166534' }}>
              {renderLatex(safeText(correctAnswer))}
            </Typography>
          </Box>
        )}

        {/* Solution Explanation (if it's a text string) */}
        {solutionText && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body1" paragraph>
              {renderLatex(safeText(solutionText))}
            </Typography>
          </Box>
        )}
        
        {/* Solution Steps */}
        {steps.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: '#166534' }}>
              Step-by-Step Solution:
            </Typography>
            {steps.map((step, index) => {
              // Handle different step structures
              const stepNumber = step.stepNumber || step.number || index + 1;
              const stepContent = safeText(step.content) || safeText(step.text) || safeText(step.explanation) || safeText(step.description) || '';
              const stepInstruction = safeText(step.instruction) || safeText(step.title) || '';
              const stepLatex = safeText(step.latex) || safeText(step.formula) || safeText(step.math) || '';
              
              // Skip empty steps
              if (!stepContent && !stepLatex && !stepInstruction) {
                return null;
              }
              
              return (
                <Box key={index} sx={{ mb: 2, pl: 2, borderLeft: '3px solid #86efac' }}>
                  <Typography variant="subtitle2" sx={{ color: '#166534', fontWeight: 600 }}>
                    Step {stepNumber}{stepInstruction ? `: ${stepInstruction}` : ''}
                  </Typography>
                  
                  {stepContent && (
                    <Typography variant="body2" sx={{ mt: 0.5, color: '#374151' }}>
                      {renderLatex(stepContent)}
                    </Typography>
                  )}
                  
                  {stepLatex && (
                    <Box sx={{ mt: 1, p: 1, bgcolor: 'white', borderRadius: 1 }}>
                      {renderLatex(stepLatex.startsWith('$') ? stepLatex : `$$${stepLatex}$$`)}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        )}

        {/* Fallback: If solution is just a string, display it */}
        {typeof currentQuestion.solution === 'string' && !steps.length && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
              {renderLatex(currentQuestion.solution)}
            </Typography>
          </Box>
        )}

        {/* Hints if available */}
        {currentQuestion.hints && currentQuestion.hints.length > 0 && (
          <Box sx={{ mt: 2, p: 1.5, bgcolor: '#fef3c7', borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ color: '#92400e' }}>
              💡 Hints:
            </Typography>
            <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
              {currentQuestion.hints.map((hint, index) => (
                <li key={index}>
                  <Typography variant="body2" sx={{ color: '#78350f' }}>
                    {safeText(hint)}
                  </Typography>
                </li>
              ))}
            </ul>
          </Box>
        )}
      </Box>
    );
  };

  if (!currentQuestion) {
    return (
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography>No question available</Typography>
          <Button onClick={fetchNewQuestion} variant="contained" sx={{ mt: 2 }}>
            Load Question
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        {/* Question Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip label={`Grade ${currentQuestion.grade || 10}`} size="small" color="primary" />
            <Chip label={currentQuestion.topic || 'Math'} size="small" />
            {currentQuestion.subtopic && <Chip label={currentQuestion.subtopic} size="small" variant="outlined" />}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {(currentQuestion.marks || currentQuestion.points) && (
              <Chip 
                icon={<Award size={16} />} 
                label={`${currentQuestion.marks || currentQuestion.points} marks`} 
                size="small" 
                color="secondary" 
              />
            )}
            <Chip 
              label={currentQuestion.difficulty || 'medium'} 
              size="small" 
              color={
                currentQuestion.difficulty === 'easy' ? 'success' : 
                currentQuestion.difficulty === 'hard' ? 'error' : 'warning'
              }
            />
            <Tooltip title={hasReported ? 'Reported' : 'Report an issue'}>
              <IconButton
                size='small'
                onClick={(e) => { e.stopPropagation(); if (!hasReported) setReportOpen(true); }}
                sx={{ ml: 0.5, color: hasReported ? '#4caf50' : '#bdbdbd', '&:hover': { color: hasReported ? '#4caf50' : '#f44336' } }}
              >
                {hasReported ? <CheckCircleOutlineIcon sx={{ fontSize: 18 }} /> : <FlagOutlinedIcon sx={{ fontSize: 18 }} />}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Question Text */}
        <Typography variant="body1" paragraph>
          {renderLatex(safeText(currentQuestion.content) || safeText(currentQuestion.question?.text) || safeText(currentQuestion.problem?.text) || safeText(currentQuestion.questionText) || safeText(currentQuestion.question) || safeText(currentQuestion.text) || '')}
        </Typography>

        {/* Question LaTeX */}
        {(currentQuestion.question?.latex || currentQuestion.latex) && (
          <Box sx={{ my: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            {renderLatex(`$$${currentQuestion.question?.latex || currentQuestion.latex}$$`)}
          </Box>
        )}
	{/* Diagram */}
        {(currentQuestion.image || currentQuestion.content?.image) && (
          <div style={{ margin: '12px 0' }}>
            <MathDiagram data={currentQuestion.image || currentQuestion.content?.image} />
          </div>
        )}
        {/* Answer Input */}
        {renderAnswerInput()}

        {/* Feedback Message */}
        {feedback && !isSubmitted && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {feedback}
          </Alert>
        )}

        {/* Solution Display */}
        {renderSolution()}
      </CardContent>

      <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
        <Box>
          {isSubmitted && (
            <Button
              variant="outlined"
              onClick={() => setShowSolution(!showSolution)}
              startIcon={<BookOpen size={16} />}
            >
              {showSolution ? 'Hide Solution' : 'View Solution'}
            </Button>
          )}
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          {isSubmitted ? (
            <Button
              variant="contained"
              onClick={fetchNewQuestion}
              color="secondary"
              disabled={loading}
            >
              {loading ? 'Loading...' : 'Next Question'}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={loading || (!userAnswer && !selectedOption)}
              color="primary"
            >
              {loading ? 'Submitting...' : 'Submit Answer'}
            </Button>
          )}
        </Box>
      </CardActions>
      <ReportContentDialog
        open={reportOpen}
        onClose={handleReportClose}
        contentItem={currentQuestion}
        contentType="question"
      />
    </Card>
  );
};

export default QuestionDisplay;
